/**
 * @file OrderStatusEditModal.tsx
 *
 * Edit-status dialog: a status select (any-to-any, preselected to the current
 * status) plus an optional reason, on Gravity's {@link StandardDialog}. Confirming
 * calls the engine's admin status-override backdoor, which relabels the order and
 * runs the target status's side effects but never moves money — so a warning is
 * shown when the cancelled/refunded labels are picked (the real Cancel/Refund
 * actions are the money-moving paths). The parent supplies the confirm handler
 * (which calls the store's `overrideOrderStatus`) and the saving / error state.
 */

import { useId, useState } from 'react';
import { Icon, StandardDialog } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../i18n/messages';
import { ORDER_STATUSES, statusLabel } from './order-status';

/** The status labels whose real actions move money; picking them only relabels here. */
const NO_MONEY_WARNING_STATUSES: ReadonlySet<string> = new Set(['cancelled', 'refunded']);

interface OrderStatusEditModalProps {
  /** The order's current status (shop vocabulary); confirm stays disabled until it changes. */
  currentStatus: string;
  saving: boolean;
  /** Optional error shown in-dialog when the status change fails. */
  errorMessage?: string;
  onConfirm: (status: string, reason: string) => void;
  onClose: () => void;
}

/**
 * Renders the edit-status dialog.
 *
 * @param props - The current status, loading/error state, and the confirm/close handlers.
 *
 * @returns The dialog element.
 */
export function OrderStatusEditModal({
  currentStatus,
  saving,
  errorMessage,
  onConfirm,
  onClose,
}: OrderStatusEditModalProps) {
  const intl = useIntl();
  const statusId = useId();
  const reasonId = useId();
  const [status, setStatus] = useState(currentStatus);
  const [reason, setReason] = useState('');

  const handleDismiss = (): void => {
    if (saving) {
      return;
    }
    onClose();
  };

  return (
    <StandardDialog
      isOpen
      title={intl.formatMessage(adminMessages.ordersEditStatusTitle)}
      onClose={handleDismiss}
      onClickAway={handleDismiss}
      onEscape={handleDismiss}
      footer={
        <div className="gv-button-group">
          <button
            className="gv-button gv-button-cancel"
            type="button"
            data-testid="order-status-edit-dismiss"
            onClick={handleDismiss}
            disabled={saving}
          >
            {intl.formatMessage(adminMessages.ordersCancel)}
          </button>
          <button
            className="gv-button gv-button-primary"
            type="button"
            data-testid="order-status-edit-confirm"
            onClick={() => onConfirm(status, reason)}
            disabled={saving || status === currentStatus}
          >
            {saving
              ? intl.formatMessage(adminMessages.ordersEditStatusLoading)
              : intl.formatMessage(adminMessages.ordersEditStatusConfirm)}
          </button>
        </div>
      }
    >
      <div className="gv-flex gv-flex-col gv-gap-md gv-w-full" data-testid="order-status-edit-modal">
        <div className="gv-form-option gv-w-full">
          <label className="gv-label" htmlFor={statusId}>
            {intl.formatMessage(adminMessages.ordersEditStatusLabel)}
          </label>
          <div className="gv-input gv-input-select gv-w-full">
            <select
              id={statusId}
              value={status}
              data-testid="order-status-edit-select"
              disabled={saving}
              onChange={(event) => setStatus(event.target.value)}
            >
              {ORDER_STATUSES.map((option) => {
                const label = statusLabel(option);
                return (
                  <option key={option} value={option}>
                    {label ? intl.formatMessage(label) : option}
                  </option>
                );
              })}
            </select>
            <Icon name="expand_more" size="small" />
          </div>
        </div>
        {status !== currentStatus && NO_MONEY_WARNING_STATUSES.has(status) && (
          <p
            className="gv-text-sm gv-state-attention"
            role="alert"
            data-testid="order-status-edit-warning"
            style={{ marginBottom: 0 }}
          >
            {intl.formatMessage(adminMessages.ordersEditStatusNoMoneyWarning)}
          </p>
        )}
        <div className="gv-form-option gv-w-full">
          <label className="gv-label" htmlFor={reasonId}>
            {intl.formatMessage(adminMessages.ordersEditStatusReasonLabel)}
          </label>
          <textarea
            id={reasonId}
            className="gv-input gv-input-textarea gv-w-full"
            rows={3}
            // The engine caps the audit reason at 500 chars (strict schema).
            maxLength={500}
            data-testid="order-status-edit-reason"
            value={reason}
            disabled={saving}
            placeholder={intl.formatMessage(adminMessages.ordersEditStatusReasonPlaceholder)}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
        {errorMessage && (
          <p
            className="gv-text-sm gv-state-critical"
            role="alert"
            data-testid="order-status-edit-error"
            style={{ marginBottom: 0 }}
          >
            {errorMessage}
          </p>
        )}
      </div>
    </StandardDialog>
  );
}
